import { Directive, Input, OnDestroy, OnInit, Optional, TemplateRef, ViewContainerRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { PermissionsService } from '../common-services/permissions.service';
import { ReadOnlyService } from '../common-services/read-only.service';
import { ReadOnlyScopeDirective } from './read-only-scope.directive';

export interface ModifyOnlyConfig {
  feature?: string;
  permission?: string | string[];
}

@Directive({
  selector: '[appModifyOnly]'
})
export class ModifyOnlyDirective implements OnInit, OnDestroy {
  private hasView = false;
  private subscription = new Subscription();
  private rawConfig: any = null;

  @Input() set appModifyOnly(config: boolean | string | string[] | ModifyOnlyConfig | '' | null) {
    this.rawConfig = config;
    this.updateView();
  }

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private readOnlyService: ReadOnlyService,
    private permissionsService: PermissionsService,
    @Optional() private scopeDirective?: ReadOnlyScopeDirective
  ) { }

  ngOnInit(): void {
    this.subscription.add(
      this.readOnlyService.isReadOnly$.subscribe(() => {
        this.updateView();
      })
    );

    if (this.scopeDirective) {
      this.subscription.add(
        this.scopeDirective.scopeChanged$.subscribe(() => {
          this.updateView();
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private updateView(): void {
    const isBlocked = this.checkIfBlocked();

    if (!isBlocked && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (isBlocked && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }

  /**
   * Determines if the current element should be blocked (hidden).
   */
  private checkIfBlocked(): boolean {
    // 1. If enclosing scope is read-only, block
    if (this.scopeDirective && this.scopeDirective.isScopeReadOnly) {
      return true;
    }

    // 2. If global read-only is active, block
    if (this.readOnlyService.isReadOnly()) {
      return true;
    }

    // 3. Inspect directive input config
    if (this.rawConfig === false) {
      return true;
    }

    if (typeof this.rawConfig === 'string' && this.rawConfig.trim().length > 0) {
      const configStr = this.rawConfig.trim();
      // If user passed a feature key (e.g. 'sub_contractor'), check if that feature is read-only
      if (this.readOnlyService.isComponentReadOnly(configStr)) {
        return true;
      }
      // If configStr is a modify permission name, check if user has that permission
      if (!this.permissionsService.hasPermission(configStr)) {
        return true;
      }
    } else if (Array.isArray(this.rawConfig) && this.rawConfig.length > 0) {
      // Must have at least one of the permissions
      const hasAny = this.rawConfig.some(p => this.permissionsService.hasPermission(p));
      if (!hasAny) {
        return true;
      }
    } else if (typeof this.rawConfig === 'object' && this.rawConfig !== null) {
      const obj = this.rawConfig as ModifyOnlyConfig;
      if (obj.feature && this.readOnlyService.isComponentReadOnly(obj.feature)) {
        return true;
      }
      if (obj.permission && !this.permissionsService.hasPermission(obj.permission)) {
        return true;
      }
    }

    return false;
  }
}
